/**
 * Kill Switch Registry (F11)
 * --------------------------
 * Runtime-flippable, localStorage-persisted toggles consumed by
 * edgeSafeFlags. Operators (Super Admin / War Room) can disable noisy
 * subsystems during the World Cup without redeploying.
 *
 * - No backend. Persistence is per-device by design (instant rollback).
 * - SSR-safe.
 * - Pure derivation; no polling.
 */
import { useSyncExternalStore } from "react";

export type KillSwitchKey =
  | "cinematic"
  | "viral"
  | "creatorHooks"
  | "overlays"
  | "screenshotSystems"
  | "radarDensity"
  | "pushAggressiveness"
  | "emotionalIntensity"
  | "animationLevel"
  | "glowEffects"
  | "blurEffects";

/**
 * "on"   → default behaviour
 * "soft" → degrade (reduced density / muted FX) but still rendered
 * "off"  → completely disabled at runtime
 */
export type KillSwitchValue = "on" | "soft" | "off";

export type KillSwitchMap = Record<KillSwitchKey, KillSwitchValue>;

const STORAGE_KEY = "vs.killSwitches.v1";

const DEFAULTS: KillSwitchMap = {
  cinematic: "on",
  viral: "on",
  creatorHooks: "on",
  overlays: "on",
  screenshotSystems: "on",
  radarDensity: "on",
  pushAggressiveness: "on",
  emotionalIntensity: "on",
  animationLevel: "on",
  glowEffects: "on",
  blurEffects: "on",
};

function load(): KillSwitchMap {
  if (typeof window === "undefined") return { ...DEFAULTS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<KillSwitchMap>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

let state: KillSwitchMap = load();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function persist() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota / privacy mode — ignore */ }
}

export function getKillSwitches(): KillSwitchMap {
  return state;
}

export function setKillSwitch(key: KillSwitchKey, value: KillSwitchValue) {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  persist();
  emit();
}

export function resetKillSwitches() {
  state = { ...DEFAULTS };
  persist();
  emit();
}

export function useKillSwitches(): KillSwitchMap {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => { listeners.delete(cb); }; },
    () => state,
    () => state,
  );
}

// Sync across tabs.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    state = load();
    emit();
  });
}